<template>
  <div class="saved-reports">
    <div class="toolbar">
      <h5>Saved Reports</h5>
      <hr />
    </div>
    <q-spinner v-if="!savedReports" color="primary" size="2.5em" />
    <div v-else class="report-list">
      <div class="header">
        <q-input
          ref="filter"
          v-model="filter"
          filled
          dense
          class="col-12"
          label="Filter reports"
          @keydown.esc="filter = null"
        >
          <template v-slot:append>
            <q-icon v-if="filter" name="clear" class="cursor-pointer" @click="filter = null" />
          </template>
        </q-input>

        <q-btn
          :color="showReportTypes ? 'tertiary' : 'grey-5'"
          size="sm"
          round
          icon="far fa-tag"
          @click="showReportTypes = !showReportTypes"
        >
          <LmTooltip> {{ showReportTypes ? 'Hide' : 'Show' }} report types </LmTooltip>
        </q-btn>
      </div>

      <span v-if="!savedReports.length">No saved reports yet</span>
      <span v-else-if="!filteredReports.length">No matching reports found</span>

      <div class="reports">
        <div v-for="report of filteredReports" :key="report.id" class="report">
          <div class="badge-section">
            <div :class="{ show: showReportTypes }" class="badge type">
              {{ report.report_type.name }}
            </div>
          </div>

          <div v-ripple class="report-details" @click="navigateToEditReport(report)">
            <div class="report-title">
              <div v-if="routerView === 'editReportPage' && reportToEdit.id === report.id" class="previewing" />
              {{ report.title }}
            </div>
            <div class="actions">
              <q-btn
                :loading="isGenerating && report.id === reportIdProcessing"
                outline
                icon="fas fa-download"
                color="positive"
                size="xs"
                @click.stop="generate(report)"
              >
                <LmTooltip>Generate and email to yourself</LmTooltip>
              </q-btn>

              <q-btn
                v-if="routerView !== 'editReportPage' || report.id !== reportToEdit.id"
                outline
                icon="fas fa-cogs"
                color="tertiary"
                size="xs"
                @click.stop="navigateToEditReport(report)"
              >
                <LmTooltip>View and edit</LmTooltip>
              </q-btn>

              <q-btn
                v-if="routerView !== 'editReportPage' || report.id !== reportToEdit.id"
                :loading="isDeleting && report.id === reportIdProcessing"
                outline
                icon="fas fa-trash"
                color="red-4"
                size="xs"
                @click.stop="deleteReport(report.id)"
              >
                <LmTooltip>Delete</LmTooltip>
              </q-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import * as apiTo from '@api/api-routes'

export default {
  data: () => ({
    filter: null,
    showReportTypes: false,
    isGenerating: false,
    isDeleting: false,
    reportIdProcessing: null
  }),

  computed: {
    ...mapState('reports', ['savedReports', 'reportToEdit', 'routerView']),

    filteredReports() {
      if (!this.filter) return this.savedReports
      return this.savedReports.filter(report =>
        report.title.toLocaleLowerCase().includes(this.filter.toLocaleLowerCase())
      )
    }
  },

  methods: {
    ...mapMutations('reports', ['reportDeleted']),

    navigateToEditReport(report) {
      const path = `/reports/${report.report_type.slug}/${report.id}/edit`
      if (this.$route.path !== path) this.$router.push(path)
    },

    generate(report) {
      if (this.isGenerating || this.isDeleting) return
      this.isGenerating = true
      this.reportIdProcessing = report.id
      apiTo
        .generateSavedReport(report.id)
        .then(response => {
          this.$store.dispatch(
            'createNotificationToast',
            {
              message: response.data.messages.default,
              color: response.data.status
            },
            { root: true }
          )
        })
        .finally(() => (this.isGenerating = false))
    },

    deleteReport(id) {
      if (this.isGenerating || this.isDeleting) return
      this.isDeleting = true
      this.reportIdProcessing = id
      apiTo
        .deleteReport(id)
        .then(() => this.reportDeleted(id))
        .finally(() => (this.isDeleting = false))
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/custom-variables'
@import '~@/styles/stylus/mixins'

.saved-reports {
  border 1px solid #e4dddd
  border-top none
  padding 10px
  text-align center
  display grid
  grid-template-rows 50px 1fr
  height 100%

  .report-list {
    display grid
    grid-template-rows 50px 1fr
    gap 10px
    overflow hidden

    .header {
      display grid
      grid-template-columns 1fr auto
      align-items center
      gap 20px
    }

    styledScrollFor('.reports');

    .reports {
      display grid
      gap 10px
      align-content start
      overflow auto

      .report {
        .badge-section {
          display grid
          grid-auto-flow column
          justify-content start
          gap 10px

          .badge {
            padding 0 10px

            &.type {
              visibility hidden
              height 0
              transition height .1s linear
            }

            &.show.type {
              visibility visible
              background $tertiary
              color white
              border-radius 3px 3px 0 0
              height 100%
            }
          }

        }

        .report-details {
          display grid
          grid-auto-flow column
          align-items center
          justify-content space-between
          padding 10px
          border-radius 3px
          border 1px solid #2c3b5326
          cursor pointer
          position relative
          transition all .2s linear

          .report-title {
            font-size 14px
            display flex
            align-items center
            text-align: left;
            margin-right: 5px

            .previewing {
              margin-right 10px
              width 10px
              height 10px
              background $tertiary
              border-radius 50%
            }

          }

          &:hover {
            background #eeeeee
          }

          &:first-child {
            margin-top 20px
          }

          .actions {
            display grid
            grid-auto-flow column
            gap 10px
          }
        }

      }
    }
  }
}
</style>
